import { AfterViewChecked, Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { IconDefinition } from '@fortawesome/fontawesome-common-types';
import { Select, Store } from '@ngxs/store';
import _ from 'lodash';
import { Observable, Subject } from 'rxjs';
import { AuthenticationState } from '../../../store/session/authentication/authentication.state';
import { filter, map, take, takeUntil, tap } from 'rxjs/operators';
import { AuthenticateWithMicrosoftEntraIdToken, SignInAction, SignOutFromMicrosoftEntraId } from '../../../store/session/authentication/authentication.actions';
import { AuthenticationSettingsSelectors } from 'src/app/store/authentication-settings/authentication-settings.selectors';
import { RequestAuthenticationSettings } from 'src/app/store/authentication-settings/authentication-settings.actions';
import { WellKnownIconsEnum } from 'src/app/helper/iconHelper';

@Component({
   selector: 'samweb-authorization-form',
   templateUrl: './authorization-form.component.html',
   styleUrls: ['./authorization-form.component.scss'],
})
export class AuthorizationFormComponent implements OnInit, AfterViewChecked, OnDestroy {
   private destroy$ = new Subject();

   public signInForm = this.formBuilder.group({
      userName: ['', Validators.required],
      password: [''],
      rememberMe: [''],
   });

   @Select(AuthenticationState.getErrorText)
   public errorText$: Observable<string>;

   @Select(AuthenticationState.getErrorIcon)
   public errorIcon$: Observable<IconDefinition>;

   @Select(AuthenticationState.getShowError)
   public showError$: Observable<boolean>;

   @Select(AuthenticationSettingsSelectors.useMicrosoftEntraIdAuthentication)
   public useMicrosoftEntraIdAuthentication$: Observable<boolean>;

   @Select(AuthenticationState.wasMicrosoftEntraIdAuthenticationAttempt)
   public wasMicrosoftEntraIdAuthenticationAttempt$: Observable<boolean>;

   public readonly microsoftIcon: WellKnownIconsEnum = WellKnownIconsEnum.MICROSOFT;

   constructor(private store: Store, private formBuilder: FormBuilder, private activatedRoute: ActivatedRoute) {}

   ngAfterViewChecked(): void {
      this.activatedRoute.queryParams
         .pipe(
            map((params) => params.autologon),
            filter((autologon) => !_.isEmpty(autologon) && autologon === 'true'),
            take(1),
            tap(() => this.signMeIn()),
            takeUntil(this.destroy$)
         )
         .subscribe();
   }

   ngOnInit(): void {
      this.activatedRoute.queryParams
         .pipe(
            map((params) => params.username),
            filter((username) => !_.isEmpty(username)),
            tap((x) => this.signInForm.controls.userName.setValue(x)),
            takeUntil(this.destroy$)
         )
         .subscribe();

      this.store.dispatch(new RequestAuthenticationSettings());
   }

   ngOnDestroy(): void {
      this.destroy$.next();
      this.destroy$.complete();
   }

   public signMeIn(): void {
      this.store.dispatch(new SignInAction(this.signInForm.value.userName, this.signInForm.value.password));
      this.signInForm.disable();
   }

   public onMicrosoftSignInButtonClicked(): void {
      const microsoftEntraIdAuthenticationSettings = this.store.selectSnapshot(AuthenticationSettingsSelectors.getMicrosoftEntraIdAuthenticationSettings);
      this.store.dispatch(new AuthenticateWithMicrosoftEntraIdToken(microsoftEntraIdAuthenticationSettings));

      this.signInForm.disable();
   }

   public onMicrosoftSignOutButtonClicked(): void {
      const microsoftEntraIdAuthenticationSettings = this.store.selectSnapshot(AuthenticationSettingsSelectors.getMicrosoftEntraIdAuthenticationSettings);
      this.store.dispatch(new SignOutFromMicrosoftEntraId(microsoftEntraIdAuthenticationSettings));
   }
}
